import React from "react";
import classnames from "classnames";
import { useField, FieldHookConfig, useFormikContext } from "formik";

export interface ISelectOption {
    key: string;
    value: string;
    text: string;
}

type Props = FieldHookConfig<string[]> & {
    label?: React.ReactNode;
    className?: string;
    disabled?: boolean;
    options: ISelectOption[];
    onChange?: (selectedKeys: string[]) => void;
    placeholder?: string;
    noMatchesText?: string;
    clearable?: boolean;
    showCaret?: boolean;
    maxMenuHeight?: number;
    /** Optional: return custom style for each pill (e.g. for series-matched colors) */
    getPillStyle?: (key: string, index: number) => React.CSSProperties;
};

export function SearchMultipleSelectField({
    label,
    className,
    disabled = false,
    options,
    onChange,
    placeholder = "Search",
    noMatchesText = "No matches",
    clearable = false,
    showCaret = false,
    maxMenuHeight = 240,
    getPillStyle,
    ...props
}: Props): React.ReactElement {
    const [field, meta, helpers] = useField<string[]>(props);
    const { setValue, setTouched } = helpers;
    const formikContext = useFormikContext();
    const submitCount = formikContext ? formikContext.submitCount : 0;

    const value: string[] = Array.isArray(field.value) ? field.value : [];

    const optionMap = React.useMemo(() => new Map(options.map((o) => [o.key, o])), [options]);
    const missingValues = value.filter((v) => !optionMap.has(v));
    const missingOptions: ISelectOption[] = missingValues.map((v) => ({
        key: v,
        value: v,
        text: `Not in list: ${v}`
    }));
    const allOptions: ISelectOption[] = React.useMemo(() => [...missingOptions, ...options], [options, missingOptions]);

    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const [activeIndex, setActiveIndex] = React.useState<number>(-1);

    const lower = query.trim().toLowerCase();
    let filtered = allOptions;
    if (lower) {
        filtered = allOptions.filter((o) => o.text.toLowerCase().includes(lower) || o.key.toLowerCase().includes(lower));
    }

    React.useEffect(() => {
        if (activeIndex >= filtered.length) setActiveIndex(filtered.length - 1);
    }, [filtered.length, activeIndex]);

    const wrapperRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!wrapperRef.current) return;
            if (!wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
                setTouched(true, true);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [setTouched]);

    const inputRef = React.useRef<HTMLInputElement>(null);
    const listRef = React.useRef<HTMLUListElement>(null);

    const commitChange = (keys: string[]) => {
        setValue(keys, true);
        onChange?.(keys);
    };

    const selectKey = (k: string) => {
        if (missingValues.includes(k)) return;
        if (value.includes(k)) {
            commitChange(value.filter((v) => v !== k));
        } else {
            commitChange([...value, k]);
        }
    };

    const removeKey = (k: string) => {
        if (!value.includes(k)) return;
        commitChange(value.filter((v) => v !== k));
    };

    const clearAll = () => {
        commitChange([]);
        inputRef.current?.focus();
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (disabled) return;

        if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " " || e.key === "ArrowUp")) {
            setOpen(true);
            setTimeout(() => setActiveIndex(0), 0);
            return;
        }

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setOpen(true);
                setActiveIndex((i) => Math.min((i < 0 ? -1 : i) + 1, filtered.length - 1));
                break;
            case "ArrowUp":
                e.preventDefault();
                setOpen(true);
                setActiveIndex((i) => Math.max((i < 0 ? filtered.length : i) - 1, 0));
                break;
            case "Enter":
                if (open && activeIndex >= 0 && activeIndex < filtered.length) {
                    e.preventDefault();
                    selectKey(filtered[activeIndex].key);
                }
                break;
            case "Escape":
                if (open) {
                    e.preventDefault();
                    setOpen(false);
                } else {
                    (e.target as HTMLInputElement).blur();
                }
                break;
            case "Backspace":
                if (query === "" && value.length > 0) {
                    commitChange(value.slice(0, -1));
                }
                break;
        }
    };

    const controlId = props.name;

    let labelNode: React.ReactNode = null;
    if (label != null) {
        if (typeof label === "string") {
            labelNode = <label htmlFor={controlId}>{label}</label>;
        } else {
            labelNode = label;
        }
    }

    const showError = (meta.touched || submitCount > 0) && meta.error;

    return (
        <div className={classnames("mb-4 w-full", className)} ref={wrapperRef}>
            {labelNode}

            <div className="relative">
                <div
                    className="form-input-combobox"
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    aria-owns={`${controlId}-listbox`}
                    aria-controls={`${controlId}-listbox`}
                    aria-disabled={disabled}
                    onClick={() => {
                        if (disabled) return;
                        setOpen(true);
                        inputRef.current?.focus();
                    }}
                >
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1 gap-y-0.5 overflow-hidden">
                        {value.map((k, idx) => {
                            const foundOption = optionMap.get(k);
                            let opt: ISelectOption = { key: k, value: k, text: k };
                            if (foundOption) {
                                opt = foundOption;
                            }
                            const isMissing = missingValues.includes(k);
                            const pillStyle = getPillStyle && !isMissing ? getPillStyle(k, idx) : undefined;
                            return (
                                <span
                                    key={k}
                                    className={classnames(
                                        "box-border inline-flex h-[1.5rem] max-w-full min-w-0 items-center rounded px-1.5 text-sm leading-none",
                                        isMissing ? "bg-red-50 text-red-700" : "bg-gray-100 text-gray-700"
                                    )}
                                    title={opt.text}
                                    style={pillStyle}
                                >
                                    <span className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">{opt.text}</span>
                                    <button
                                        type="button"
                                        className="ml-0.5 flex h-full cursor-pointer items-center border-0 bg-transparent p-0 text-sm leading-none text-gray-500 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeKey(k);
                                        }}
                                        aria-label={`Remove ${opt.text}`}
                                        disabled={disabled}
                                    >
                                        ×
                                    </button>
                                </span>
                            );
                        })}

                        <input
                            ref={inputRef}
                            id={controlId}
                            className="h-[1.5rem] min-w-16 flex-1 border-0 bg-transparent p-0 text-base leading-[1.5rem] text-gray-700 shadow-none outline-none placeholder:text-gray-400"
                            placeholder={value.length === 0 ? placeholder : ""}
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                if (!open) setOpen(true);
                            }}
                            onKeyDown={onKeyDown}
                            onBlur={() => {
                                setQuery("");
                                setTouched(true, true);
                            }}
                            disabled={disabled}
                            autoComplete="off"
                        />
                    </div>

                    {((clearable && value.length > 0) || showCaret) && (
                        <div className="ml-2 flex items-center gap-2">
                            {clearable && value.length > 0 && (
                                <button
                                    type="button"
                                    className="cursor-pointer border-0 bg-transparent p-0 text-xl leading-none text-gray-500 hover:text-gray-900"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        clearAll();
                                    }}
                                    aria-label="Clear"
                                    disabled={disabled}
                                >
                                    ×
                                </button>
                            )}
                            {showCaret && (
                                <button
                                    type="button"
                                    className={classnames(
                                        "cursor-pointer border-0 bg-transparent p-0 text-base leading-none text-gray-500 transition-transform duration-150 hover:text-gray-900",
                                        open && "rotate-180"
                                    )}
                                    aria-label="Toggle menu"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpen((o) => !o);
                                        inputRef.current?.focus();
                                    }}
                                    disabled={disabled}
                                >
                                    ▾
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {open && (
                    <ul
                        id={`${controlId}-listbox`}
                        role="listbox"
                        className="absolute top-[calc(100%+0.125rem)] right-0 left-0 z-[1000] m-0 list-none overflow-auto rounded border border-gray-300 bg-white p-0"
                        style={{ maxHeight: maxMenuHeight }}
                        ref={listRef}
                    >
                        {filtered.length === 0 ? (
                            <li className="px-2 py-1.5 text-gray-500 italic" aria-disabled="true">
                                {noMatchesText}
                            </li>
                        ) : (
                            filtered.map((o, idx) => {
                                const selected = value.includes(o.key);
                                const missing = missingValues.includes(o.key);
                                const active = idx === activeIndex;
                                return (
                                    <li
                                        key={o.key}
                                        role="option"
                                        aria-selected={selected}
                                        className={classnames(
                                            "flex items-center justify-between px-2 py-1.5 text-gray-700",
                                            missing ? "cursor-not-allowed bg-red-50 text-red-700" : "cursor-pointer",
                                            !missing && (active || selected) && "bg-gray-100",
                                            selected && "font-semibold",
                                            !missing && selected && active && "bg-gray-200"
                                        )}
                                        onMouseEnter={() => setActiveIndex(idx)}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => selectKey(o.key)}
                                        title={missing ? "Value not in list" : undefined}
                                    >
                                        <span>{o.text}</span>
                                        {missing ? (
                                            <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">missing</span>
                                        ) : selected ? (
                                            <span className="ml-2 text-gray-700">✓</span>
                                        ) : null}
                                    </li>
                                );
                            })
                        )}
                    </ul>
                )}
            </div>

            {showError ? <div className="form-field-error mt-1.5">{meta.error as React.ReactNode}</div> : null}
        </div>
    );
}
